import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
    Share2,
    Link as LinkIcon,
    Linkedin,
    Twitter,
    Mail,
    MessageCircle,
    Smartphone,
} from "lucide-react";

/**
 * Reusable share popover for job listings.
 *
 * Props:
 *   - job:      { title, location, salary_range }
 *   - url:      absolute URL to share
 *   - variant:  "button" (default) — full labelled pill;
 *               "icon"             — 32px icon-only (for card corners)
 *   - testIdPrefix: base test-id — the trigger uses this literal,
 *                   options append "-{platform}"
 *   - menuTestId:   test-id for the popover container
 *   - anchor:   "right" (default) | "left" — popover horizontal alignment
 *   - onOpenChange?: (open) => void
 */
export default function ShareMenu({
    job,
    url,
    variant = "button",
    testIdPrefix = "job-share",
    menuTestId = "job-share-menu",
    anchor = "right",
    onOpenChange,
}) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        onOpenChange?.(open);
        if (!open) return;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, onOpenChange]);

    const stop = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

    const title = job?.title || "";
    const location = job?.location || "";
    const salary = job?.salary_range || "";

    const messages = {
        whatsapp: `Job opening at EB Construction — ${title} in ${location}. Apply here: ${url}`,
        twitter: `Now hiring: ${title} at EB Construction. ${url}`,
        emailSubject: `Job opening: ${title} at EB Construction`,
        emailBody:
            `Hi,\n\n` +
            `Thought you might be interested in this role:\n\n` +
            `${title}\n${location}${salary ? `\n${salary}` : ""}\n\n` +
            `Details and apply: ${url}\n\n` +
            `— Shared from EB Construction Careers`,
    };

    const openExternal = (href) => {
        window.open(href, "_blank", "noopener,noreferrer");
    };

    const doCopy = async () => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                // Fallback for older browsers
                const ta = document.createElement("textarea");
                ta.value = url;
                ta.setAttribute("readonly", "");
                ta.style.position = "absolute";
                ta.style.left = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            toast.success("Link copied");
        } catch {
            toast.error("Could not copy link");
        }
    };

    const doNativeShare = async () => {
        try {
            await navigator.share({
                title: `${title} — EB Construction`,
                text: messages.whatsapp,
                url,
            });
        } catch {
            // user dismissed; ignore
        }
    };

    const handle = (fn) => (e) => {
        stop(e);
        setOpen(false);
        fn();
    };

    const trigger =
        variant === "icon" ? (
            <button
                type="button"
                onClick={(e) => {
                    stop(e);
                    setOpen((v) => !v);
                }}
                aria-label={`Share ${title}`}
                aria-expanded={open}
                className="grid h-8 w-8 place-items-center border border-brand-line text-brand-steel transition-colors hover:border-brand-gold hover:text-brand-gold"
                data-testid={testIdPrefix}
            >
                <Share2 className="h-4 w-4" />
            </button>
        ) : (
            <button
                type="button"
                onClick={(e) => {
                    stop(e);
                    setOpen((v) => !v);
                }}
                aria-expanded={open}
                className="inline-flex items-center gap-2 border border-brand-gold px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-gold transition-colors hover:bg-brand-gold hover:text-brand-charcoal"
                data-testid={testIdPrefix}
            >
                <Share2 className="h-4 w-4" />
                Share this job
            </button>
        );

    const anchorClass = anchor === "left" ? "left-0" : "right-0";

    return (
        <div
            ref={rootRef}
            className="relative inline-block"
            onClick={stop}
        >
            {trigger}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                        role="menu"
                        className={`absolute ${anchorClass} top-[calc(100%+8px)] z-30 w-64 border border-brand-line bg-white p-2 shadow-lg`}
                        data-testid={menuTestId}
                    >
                        {canNativeShare && (
                            <ShareRow
                                Icon={Smartphone}
                                label="Share via device…"
                                onClick={handle(doNativeShare)}
                                testId={`${testIdPrefix}-option-native`}
                            />
                        )}
                        <ShareRow
                            Icon={LinkIcon}
                            label="Copy link"
                            onClick={handle(doCopy)}
                            testId={`${testIdPrefix}-option-copy`}
                        />
                        <ShareRow
                            Icon={MessageCircle}
                            label="WhatsApp"
                            onClick={handle(() =>
                                openExternal(
                                    `https://wa.me/?text=${encodeURIComponent(messages.whatsapp)}`,
                                ),
                            )}
                            testId={`${testIdPrefix}-option-whatsapp`}
                        />
                        <ShareRow
                            Icon={Linkedin}
                            label="LinkedIn"
                            onClick={handle(() =>
                                openExternal(
                                    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
                                ),
                            )}
                            testId={`${testIdPrefix}-option-linkedin`}
                        />
                        <ShareRow
                            Icon={Twitter}
                            label="X (Twitter)"
                            onClick={handle(() =>
                                openExternal(
                                    `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                                        messages.twitter,
                                    )}&url=${encodeURIComponent(url)}`,
                                ),
                            )}
                            testId={`${testIdPrefix}-option-twitter`}
                        />
                        <ShareRow
                            Icon={Mail}
                            label="Email"
                            onClick={handle(() => {
                                window.location.href = `mailto:?subject=${encodeURIComponent(
                                    messages.emailSubject,
                                )}&body=${encodeURIComponent(messages.emailBody)}`;
                            })}
                            testId={`${testIdPrefix}-option-email`}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function ShareRow({ Icon, label, onClick, testId }) {
    return (
        <button
            type="button"
            role="menuitem"
            onClick={onClick}
            className="flex w-full items-center gap-3 px-3 py-2 text-left font-body text-sm text-brand-charcoal transition-colors hover:bg-brand-offwhite hover:text-brand-gold"
            data-testid={testId}
        >
            <Icon className="h-4 w-4 text-brand-gold" />
            {label}
        </button>
    );
}
